<template>
    <validation-observer ref="checkForm" slim #default="{ invalid }">
        <form class="checkout-form" id="checkout_form" @submit.prevent="register(invalid)">
            <div class="checkout-form__inner" v-if="!isFormSent || currentUserExistFlow === 'orderFlow'">
                <input type="hidden" name="csrf" :value="csrf">
                <div class="order-form__row row">
                    <div class="col-12" :class="{'col-sm-6': isCodeSent && !isPhoneConfirmed}">
                        <validation-provider rules="required|phone" #default="{ valid, errors }">
                            <text-input
                                label="Телефон"
                                name="phone"
                                mask="phone"
                                :class="{
                                        'has-error': isPhoneInvalid || errors.length,
                                        'has-success': isPhoneConfirmed && !errors.length,
                                    }"
                                :status="getPhoneStatusContextual(errors)"
                                :valid="checkPhoneValidity(valid)"
                                :readonly="isPhoneConfirmed"
                                v-model="formModel.phone"
                                @input="onPhoneInput"
                                @keypress.enter.native.prevent="sendCode()"
                                inputmode="numeric"
                            ></text-input>
                        </validation-provider>
                    </div>
                    <div class="col-12 col-sm-6" v-if="!isPhoneConfirmed || sendCodeAvailable">
                        <validation-provider rules="required|smsCode" #default="{ valid,errors }">
                            <text-input
                                label="Введите код из СМС"
                                name="smscode"
                                :class="{
                                        'has-error': isCodeInvalid || errors.length,
                                        'has-success': isPhoneConfirmed && !errors.length,
                                    }"
                                :status="codeStatus || errors[0]"
                                :valid="checkSmsCodeValidity(valid)"
                                v-if="showCodeInput"
                                v-model="formModel.smscode"
                                @input="onCodeInput"
                                @keypress.enter.native.prevent="phoneAuth"
                                inputmode="numeric"
                                ref="code"
                                :autocomplete="'one-time-code'"
                            ></text-input>
                        </validation-provider>
                    </div>

                    <div class="col-12 col-sm-6 checkout-form__code-info" v-if="showCodeInput">
                        <p class="checkout-form__request-code"
                           v-if="!countdown">
                            <span @click="sendCode()">Выслать код повторно</span>
                        </p>
                        <div class="checkout-form__hint"
                             v-if="countdown">
                            <p>Выслать код повторно через <b>{{ countdown }} с.</b></p>
                        </div>
                    </div>
                    <template>
                        <div class="col-12">
                            <validation-provider rules="required|nameRu" #default="{ errors }">
                                <text-input
                                    :class="{
                                        'has-error': errors.length,
                                    }"
                                    :label="'Имя'"
                                    :status="errors[0]"
                                    name="UF_NAME"
                                    v-model="formModel.UF_NAME"
                                ></text-input>
                            </validation-provider>
                        </div>
                        <div class="col-12">
                            <validation-provider rules="required|email" #default="{ errors }">
                                <text-input
                                    label="Электронная почта"
                                    name="UF_EMAIL"
                                    :class="{
                                            'has-error': emailStatus || errors.length,
                                            'has-success': isEmailConfirm(errors),
                                            'has-loading': isEmailChecking,
                                        }"
                                    :status="emailStatus || errors[0]"
                                    v-model="formModel.UF_EMAIL"
                                    inputmode="email"
                                ></text-input>
                            </validation-provider>
                        </div>
                    </template>
                </div>
            </div>

            <slot name="success"></slot>
        </form>
    </validation-observer>
</template>

<script>
import TextInput from '../fields/text-input';
import { dadataMixin } from '../../../mixins/dadata';
import { VAT_ITEMS } from '../../../constants';

export default {
    name: 'checkout-form',

    components: {
        'text-input': TextInput,
    },

    mixins: [dadataMixin],

    props: {
        submitButtonText: {
            type: String,
            default: 'Зарегистрироваться',
        },

        currentUserExistFlow: {
            type: String,
            default: 'regFlow',
        },
    },

    data() {
        return {
            csrf: BX.bitrix_sessid(),
            isMobile: BX.browser.IsMobile(),
            isFormSent: false,

            formModel: {
                UF_ADDRESSES_EQUAL: 'Y',
                UF_COMPANY_ADR: '',
            },

            requestErrors: null,
            formStatus: '',
            phoneStatus: '',
            codeStatus: '',

            isGift: false,
            isCompany: window.location.hash === '#company',
            isRegistration: false,
            isCodeSent: false,
            isCodeInvalid: false,
            isPhoneInvalid: false,
            isPhoneConfirmed: false,
            isEmailConfirmed: false,
            phonePrevious: null,
            phoneValid: null,
            smsCodeValid: null,
            isSameAddress: false,

            vatItems: VAT_ITEMS,

            codeTimeout: 60,
            countdown: 0,
            countdownInterval: null,

            isLoading: false,

            isEmailExist: true,
            isEmailChecking: false,
            emailStatus: null,
            emailCheckTimer: null,
            emailCheckTimeout: 1000,

            USER_EXIST_FLOW: {
                regFlow: () => { this.phoneStatus = 'Такой пользователь уже существует'; },
                orderFlow: () => { this.sendCode('user'); },
            },
        };
    },

    computed: {
        isSendCodeBtnDisabled() {
            return !this.formModel.phone || this.isLoading;
        },

        isConfirmCodeBtnDisabled() {
            return !this.formModel.smscode || this.isLoading;
        },

        formData() {
            const formData = new FormData();
            Object.keys(this.formModel).forEach((key) => {
                if (key === 'UF_ADDRESS') {
                    formData.append('UF_ADDRESS[]', this.formModel[key]);
                } else {
                    formData.append(key, this.formModel[key]);
                }
            });
            formData.set('UF_PHONE', this.formModel.phone);
            return formData;
        },

        email() {
            return this.formModel.UF_EMAIL;
        },

        companyAddress() {
            return this.formModel.UF_COMPANY_ADR;
        },

        isAddressesEqual() {
            return this.formModel.UF_ADDRESSES_EQUAL;
        },

        sendCodeAvailable() {
            return this.formModel.phone !== this.phonePrevious;
        },

        isPhoneChange() {
            return !this.isPhoneInvalid && this.phonePrevious && this.isCodeSent && this.formModel.phone !== this.phonePrevious;
        },

        showCodeInput() {
            return (this.isCodeSent && !this.isPhoneConfirmed) && !this.isPhoneChange;
        },
    },

    methods: {
        checkPhoneValidity(valid) {
            this.phoneValid = valid;
            return this.phoneValid;
        },

        checkSmsCodeValidity(valid) {
            this.smsCodeValid = valid;
            return this.smsCodeValid;
        },

        getPhoneStatusContextual(errors) {
            if (!errors.length && !this.phoneStatus && this.isMobile) {
                return 'Телефон для входа в личный кабинет';
            }

            return this.phoneStatus || errors[0];
        },

        onPhoneInput() {
            this.isPhoneInvalid = false;
            this.phoneStatus = '';
        },

        onCodeInput() {
            this.isCodeInvalid = false;
            this.codeStatus = '';
        },

        onChangeTypeCheckbox(value, type) {
            if (value && type === 'company') {
                this.isGift = false;
            } else if (value && type === 'gift') {
                this.isCompany = false;
            }
        },

        /**
         * Обратный отсчет до повторной отправки кода
         */
        resetCountdown() {
            this.countdown = +this.codeTimeout;
            this.countdownInterval = setInterval(() => {
                if (this.countdown > 0) {
                    this.countdown -= 1;
                } else {
                    clearInterval(this.countdownInterval);
                    this.countdownInterval = null;
                    this.phoneStatus = '';
                }
            }, 1000);
        },

        sendCode(flow = 'register') {
            if (this.isSendCodeBtnDisabled) {
                return;
            }

            if (this.formModel.phone) {
                this.isLoading = true;
                this.formData.append('csrf', BX.bitrix_sessid());
                fetch(`/api/${flow}/send-code/`, {
                    method: 'POST',
                    body: this.formData,
                })
                    .then((response) => response.json())
                    .then((result) => {
                        const isSuccess = result.status === 'success';

                        this.isLoading = false;
                        this.isCodeSent = isSuccess;
                        this.isPhoneInvalid = !isSuccess;
                        this.phonePrevious = this.formModel.phone;

                        if (result.message === 'phone_exists') {
                            this.USER_EXIST_FLOW[this.currentUserExistFlow]();
                            return;
                        }

                        this.phoneStatus = isSuccess ? 'Код подтверждения отправлен' : result.message;

                        if (isSuccess) {
                            clearInterval(this.countdownInterval);
                            this.resetCountdown();
                        }
                    });
            }
        },

        phoneAuth() {
            if (this.formModel.phone && this.formModel.smscode) {
                this.isLoading = true;

                fetch('/api/user/phone-auth/', {
                    method: 'POST',
                    body: this.formData,
                })
                    .then((response) => response.json())
                    .then((result) => {
                        const isSuccess = result.status === 'success';
                        this.isCodeInvalid = !isSuccess;

                        if (isSuccess) {
                            // Успешно вошли - перезагрузить страницу, чтобы обновить данные пользователя
                            window.location.reload();
                            return;
                        }

                        this.isLoading = false;

                        if (result.message === 'Пользователь не найден') {
                            // Пользователь не найден - включить режим регистрации
                            this.isRegistration = true;
                            this.confirmCode();
                        } else {
                            // Пользователь существует, но код введён неверно
                            this.codeStatus = result.message;
                        }
                    });
            }
        },

        confirmCode() {
            if (this.formModel.phone && this.formModel.smscode) {
                this.isLoading = true;

                const formData = new FormData();
                formData.append('phone', this.formModel.phone);
                formData.append('smscode', this.formModel.smscode);

                fetch('/api/user/confirm-code/', {
                    method: 'POST',
                    body: formData,
                })
                    .then((response) => response.json())
                    .then((result) => {
                        const isSuccess = result.status === 'success';
                        this.isLoading = false;
                        this.isCodeInvalid = !isSuccess;
                        this.isPhoneConfirmed = isSuccess;
                        this.codeStatus = isSuccess ? 'Вход подтверждён' : result.message;
                        this.phoneStatus = isSuccess ? 'Телефон подтверждён' : '';

                        if (isSuccess) {
                            clearInterval(this.countdownInterval);
                            this.countdownInterval = null;
                        }
                    });
            }
        },

        registerErrorHandler(result) {
            this.isLoading = false;
            this.formStatus = 'Ошибка. Проверьте корректность вводимых данных.';

            if (!result) {
                throw new Error('Неверный формат данных');
            }

            this.requestErrors = result?.data?.FIELDS;

            if (this.requestErrors) {
                this.$refs.checkForm.setErrors(this.requestErrors); // функция валидатора
                return;
            }

            if (result.message) {
                this.formStatus = result.message;
            }
        },

        register(invalid) {
            if (invalid) return;

            const url = `/api/user/register-${this.isCompany ? 'ur' : 'phiz'}/`;
            const formDataReg = this.formData;
            formDataReg.delete('phone');
            formDataReg.delete('smscode');
            if (this.formData.get('UF_ADDRESSES_EQUAL') === 'false') this.formData.delete('UF_ADDRESSES_EQUAL');
            this.isLoading = true;

            fetch(url, {
                method: 'POST',
                body: formDataReg,
            })
                .then((response) => response.json())
                .then((result) => {
                    const isSuccess = result.status === 'success';
                    const isError = result.status === 'error';

                    if (isError) {
                        this.registerErrorHandler(result);
                        return;
                    }

                    if (isSuccess) {
                        this.isSuccess = '';
                        this.isFormSent = true;
                        this.$emit('success');
                        return;
                    }

                    this.formStatus = result.message;

                    throw Error('Ошибка авторизации');
                })
                .catch(this.registerErrorHandler);
        },

        /**
         * Проверка наличия в базе уже зарегистрированного email
         */
        checkEmail() {
            const url = '/api/register/email/';

            const formData = new FormData();
            formData.append('email', this.email);

            this.isEmailChecking = true;

            fetch(url, {
                method: 'POST',
                body: formData,
            })
                .then((response) => response.json())
                .then((result) => {
                    if (result.message === 'email_exists') {
                        this.isEmailExist = true;
                        this.emailStatus = 'Пользователь с таким email уже зарегистрирован';
                    } else {
                        this.isEmailExist = false;
                    }

                    this.isEmailChecking = false;
                });
        },

        isEmailConfirm(validationErrors) {
            this.isEmailConfirmed = !!(this.formModel.UF_EMAIL && !validationErrors.length && !this.isEmailChecking && !this.isEmailExist);
            return this.formModel.UF_EMAIL && !validationErrors.length && !this.isEmailChecking && !this.isEmailExist;
        },
    },

    watch: {
        formModel: {
            immediate: true,
            deep: true,
            handler() {
                this.formStatus = null; // сброс ошибки формы
            },
        },

        isGiftChecked: {
            immediate: true,
            handler(val) {
                if (!val) {
                    this.formStatus = null;
                }
            },
        },

        phoneValid(val) {
            if (val) {
                const formData = new FormData();
                formData.append('phone', this.formModel.phone);
                formData.append('csrf', this.csrf);

                fetch('/api/register/send-code/', {
                    method: 'POST',
                    body: formData,
                })
                    .then((response) => response.json())
                    .then((result) => {
                        const isSuccess = result.status === 'success';

                        this.isLoading = false;
                        this.isCodeSent = isSuccess;
                        this.isPhoneInvalid = !isSuccess;
                        this.phonePrevious = this.formModel.phone;

                        if (result.message === 'phone_exists') {
                            this.USER_EXIST_FLOW[this.currentUserExistFlow]();
                            return;
                        }

                        this.phoneStatus = isSuccess ? 'Код подтверждения отправлен' : result.message;

                        if (isSuccess) {
                            clearInterval(this.countdownInterval);
                            this.resetCountdown();
                        }
                    });
            }
        },

        smsCodeValid(val) {
            if (val) {
                const formData = new FormData();
                formData.append('phone', this.formModel.phone);
                formData.append('smscode', this.formModel.smscode);

                fetch('/api/user/phone-auth/', {
                    method: 'POST',
                    body: this.formData,
                })
                    .then((response) => response.json())
                    .then((result) => {
                        const isSuccess = result.status === 'success';
                        this.isCodeInvalid = !isSuccess;

                        if (isSuccess) {
                            // Успешно вошли - перезагрузить страницу, чтобы обновить данные пользователя
                            window.location.reload();
                            return;
                        }

                        this.isLoading = false;

                        if (result.message === 'Пользователь не найден') {
                            // Пользователь не найден - включить режим регистрации
                            this.isRegistration = true;
                            this.confirmCode();
                        } else {
                            // Пользователь существует, но код введён неверно
                            this.codeStatus = result.message;
                        }
                    });
            }
        },

        isCodeSent(value) {
            if (value) {
                this.$nextTick(() => {
                    try {
                        this.$refs.code.focus();
                    } catch (e) {
                        //
                    }
                });
            }
        },

        email(value) {
            clearTimeout(this.emailCheckTimer);

            if (value) {
                this.emailStatus = null;
                this.emailCheckTimer = setTimeout(this.checkEmail, this.emailCheckTimeout);
            }
        },

        // Если адрес компании изменился, и чекбокс на совпадение адресов стоит,
        // то ставим значение фактического адреса из адреса компании
        companyAddress(value) {
            if (value && this.formModel.UF_ADDRESSES_EQUAL) {
                this.formModel.UF_ADDRESS = value;
            }
        },

        // Если чекбокс на совпадение адресов стоит,
        // то ставим значение фактического адреса из адреса компании
        isAddressesEqual() {
            if (this.formModel.UF_ADDRESSES_EQUAL) {
                this.formModel.UF_ADDRESS = this.formModel.UF_COMPANY_ADR;
            } else {
                this.formModel.UF_ADDRESS = '';
            }
        },
    },
};
</script>
